import { computed, ref, watch } from 'vue'
import { useUserStore } from '@/manage/stores/user'
import { defineStore } from 'pinia'
import { userApi } from '@/manage/api/user'
import type { UserJobListPublic } from '@/manage/types/user'

const parseJobTimestamp = (value?: string | null) => {
  const time = Date.parse(String(value || ''))
  return Number.isFinite(time) ? time : 0
}

export const getJobRecentTime = (job: UserJobListPublic) =>
  Math.max(
    parseJobTimestamp(job.updated_at),
    parseJobTimestamp(job.created_at),
    parseJobTimestamp(job.open_at),
    parseJobTimestamp(job.close_at),
  )

export const sortJobsByRecentTime = (jobs: UserJobListPublic[]) =>
  [...jobs].sort((left, right) => {
    const timeDelta = getJobRecentTime(right) - getJobRecentTime(left)
    if (timeDelta !== 0) return timeDelta
    return String(right.id || '').localeCompare(String(left.id || ''))
  })

export const useJobStore = defineStore('job', () => {
  const userStore = useUserStore()
  let generation = 0
  const jobs = ref<UserJobListPublic[]>([])
  const loading = ref(false)
  const updatedAt = ref<number | null>(null)
  let jobsPromise: Promise<UserJobListPublic[]> | null = null

  const recentJobs = computed(() => sortJobsByRecentTime(jobs.value))
  const openJobs = computed(() => jobs.value.filter((job) => !job.close_at))

  const setJobs = (items: UserJobListPublic[]) => {
    jobs.value = Array.isArray(items) ? sortJobsByRecentTime(items) : []
    updatedAt.value = Date.now()
  }

  const fetchJobs = async (force = false) => {
    if (!force && jobs.value.length > 0) return jobs.value
    if (jobsPromise) return jobsPromise

    loading.value = true
    const requestGeneration = generation
    jobsPromise = (async () => {
      try {
        const response = await userApi.getUserJobs()
        if (requestGeneration === generation) setJobs(response.jobs || [])
        return jobs.value
      } finally {
        if (requestGeneration === generation) {
          loading.value = false
          jobsPromise = null
        }
      }
    })()

    return jobsPromise
  }

  const findJobById = (jobId: string) =>
    jobs.value.find((job) => String(job.id || '').trim() === String(jobId || '').trim())

  const patchJobLocal = (jobId: string, patch: Partial<UserJobListPublic>) => {
    const targetId = String(jobId || '').trim()
    if (!targetId) return

    let changed = false
    const nextJobs = jobs.value.map((job) => {
      if (String(job.id || '').trim() !== targetId) return job
      changed = true
      return {
        ...job,
        ...patch,
      }
    })

    if (changed) {
      setJobs(nextJobs)
    }
  }

  const removeJobLocal = (jobId: string) => {
    const targetId = String(jobId || '').trim()
    const nextJobs = jobs.value.filter((job) => String(job.id || '').trim() !== targetId)
    if (nextJobs.length !== jobs.value.length) {
      setJobs(nextJobs)
    }
  }

  const reset = () => {
    generation++
    jobs.value = []
    loading.value = false
    updatedAt.value = null
    jobsPromise = null
  }

  watch(() => [userStore.user?.sub, userStore.isAdminModeActive], reset, { flush: 'sync' })

  return {
    jobs,
    recentJobs,
    openJobs,
    loading,
    updatedAt,
    setJobs,
    fetchJobs,
    findJobById,
    patchJobLocal,
    removeJobLocal,
    reset,
  }
})
